import type { MouseEvent } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SHIPMENT_STAGES, useShipmentTariffEditor } from '#/entities/shipment'
import { MarketRateDialog } from '#/features/market-rate'
import { cn } from '#/shared/lib/utils'
import { ArrowRightIcon, PriceSearchIcon } from '#/shared/ui/icons'

/**
 * Lead card footer: an editable tariff with a market-rate lookup that, on save,
 * promotes the lead to a quote — the card equivalent of `ShipmentTariffCell`.
 */
export function LeadFooter({
  shipmentId,
  tariff,
  deposit,
}: {
  shipmentId: string
  tariff?: number
  deposit?: number
}) {
  const { t } = useTranslation()
  const editor = useShipmentTariffEditor({
    shipmentId,
    deposit,
    convertToStage: SHIPMENT_STAGES.QUOTE,
  })
  const { marketRate, seededPrice } = editor
  const [value, setValue] = useState(tariff?.toString() ?? '')

  useEffect(() => {
    if (seededPrice == null) return
    setValue(String(seededPrice))
  }, [seededPrice])

  const disabled = value.trim().length === 0 || editor.isPending

  return (
    <div
      onClick={(event: MouseEvent) => event.stopPropagation()}
      className="flex items-center justify-between gap-2.5"
    >
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={() => marketRate.setOpen(true)}
          aria-label={t('table.marketRate.open')}
          className="flex size-5 cursor-pointer items-center justify-center rounded hover:bg-gray-100"
        >
          <PriceSearchIcon className="size-3.5 text-orange-500" />
        </button>
        <div className="flex h-10 w-20 items-center gap-0.5 rounded-md border border-gray-200 px-1.25 text-base font-medium text-gray-900">
          <span>$</span>
          <input
            type="number"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            disabled={editor.isPending}
            className="w-full appearance-none bg-transparent outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
        </div>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => editor.save(Number(value))}
        className={cn(
          'flex h-10 items-center gap-2.5 rounded-md px-4 text-sm font-medium text-white',
          disabled
            ? 'cursor-not-allowed bg-primary/50'
            : 'cursor-pointer bg-primary hover:bg-primary/90',
        )}
      >
        {editor.isPending ? t('table.saving') : t('shipmentCard.saveToQuote')}
        <ArrowRightIcon className="size-3.5" />
      </button>
      <MarketRateDialog
        open={marketRate.open}
        onOpenChange={marketRate.setOpen}
        stats={marketRate.stats}
        rows={marketRate.rows}
        isLoading={marketRate.isLoading}
        errorMessage={marketRate.errorMessage}
        onUseRate={marketRate.onUseRate}
      />
    </div>
  )
}
